import FlexContainer from 'components/atomic/atoms/flex-container';
import CircularIconLabel from 'components/atomic/molecules/circular-icon-label';
import IconLabelCard from 'components/atomic/molecules/icon-label-card';
import { BedIcon, BathIcon, SqftIcon, CarIcon } from 'components/atomic/atoms/icons';
import { useBedroomCount } from 'ui/bedrooms';
import { useBathroomCount } from 'ui/bathrooms';
import { useSquareFootage } from 'ui/sqft';
import { useParkingCount } from 'ui/parking';
import { validateVariant } from 'ui/utils';

export const STATS_VARIANTS = {
  HERO_MOBILE: 'hero-mobile',
  HERO_DESKTOP: 'hero-desktop',
  CARD: 'card',
} as const;

export type StatsVariant = typeof STATS_VARIANTS[keyof typeof STATS_VARIANTS];

const VARIANT_CONFIG = {
  [STATS_VARIANTS.HERO_MOBILE]: {
    StatComponent: CircularIconLabel,
    format: 'short' as const,
    gap: 1,
    justify: 'space-around' as const,
  },
  [STATS_VARIANTS.HERO_DESKTOP]: {
    StatComponent: IconLabelCard,
    format: 'long' as const,
    gap: '12px',
    justify: 'flex-start' as const,
  },
  [STATS_VARIANTS.CARD]: {
    StatComponent: CircularIconLabel,
    format: 'short' as const,
    gap: 1,
    justify: 'space-around' as const,
  },
};

export interface StatsProps {
  bedrooms: number | null;
  bathrooms: number | null;
  squareFootage: { min: number; max: number } | null;
  parking: number | null;
  variant: StatsVariant;
  blur?: boolean;
}

/**
 * Stats UI component
 * 
 * Displays listing stats (beds, baths, sqft, parking) in different layouts.
 * Variant is required - no default assumed.
 */
export function Stats({
  bedrooms,
  bathrooms,
  squareFootage,
  parking,
  variant,
  blur,
}: StatsProps) {
  const config = VARIANT_CONFIG[variant];
  const format = config?.format ?? 'short';

  const bedsData = useBedroomCount({ bedrooms, format });
  const bathsData = useBathroomCount({ bathrooms, format });
  const sqftData = useSquareFootage({ squareFootage });
  const parkingData = useParkingCount({ parking });

  if (!validateVariant('Stats', variant, STATS_VARIANTS)) {
    return null;
  }

  const { StatComponent, gap, justify } = config;

  return (
    <FlexContainer
      direction="row"
      justify={justify}
      align="center"
      gap={gap}
      wrap
      sx={{ width: '100%' }}
      data-testid="ui-stats"
    >
      <StatComponent icon={<BedIcon />} label={bedsData.formatted} blur={blur} />
      <StatComponent icon={<BathIcon />} label={bathsData.formatted} blur={blur} />
      <StatComponent icon={<SqftIcon />} label={sqftData.formatted} blur={blur} />
      <StatComponent icon={<CarIcon />} label={parkingData.formatted} blur={blur} />
    </FlexContainer>
  );
}
